import { Button, EButtonColorVariant, EToastVariant, LinkButton } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ErrorBoundary from '@/components/ErrorBoundary';
import { GuidedSearchComponent } from '@/components/guided-search/GuidedSearchComponent';
import {
  EGuidedSearchComponentType,
  TGuidedSearchComponent,
} from '@/components/guided-search/types';
import { useGuidedSearchComponents } from '@/components/guided-search/useGuidedSearchComponents';
import { EHeaderTab } from '@/components/ui/SearchHeader/SearchHeader';
import { useToast } from '@/hooks/useToast';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import { getHeaderSearchFilters } from '@/redux/selectors/globalHeader';
import { trackRenterSearchedEvent } from '@/services/analytics/search';
import type { IRenterSearchedInfo } from '@/services/analytics/search/types';
import { ERentalType } from '@/services/analytics/types';
import { EDeliveryOption } from '@/services/types/core/delivery.types';
import { LocationGroupType } from '@/utility/surfacedLocation';

import {
  LocalityGuidedSearchProvider,
  useLocalityGuidedSearchContext,
} from './LocalityGuidedSearchContext';
import TowableVehicleSelector from './TowableVehicleSelector';

interface InlineGuidedSearchProps {
  initialParams?: Record<string, unknown>;
  bounds?: Record<string, string>;
  searchTitle: string;
  locality?: {
    name: string;
    address: string;
  };
  source?: IRenterSearchedInfo['source'];
  guidedSearchData?: any; // Server-side data to prevent client-side loading
}

const InlineGuidedSearchContent: React.FC<{
  locality?: {
    name: string;
    address: string;
  };
  buildParamsFromSelections: (
    selections: Record<string, Record<string, string | string[] | undefined>>,
  ) => Record<string, string>;
  headerAddress?: string;
}> = ({ locality, buildParamsFromSelections, headerAddress }) => {
  const intl = useIntl();
  const {
    goToPrevStep,
    goToNextStep,
    applySelection: _originalApplySelection,
    currentStep,
    numberOfSteps,
    currentStepComponent,
    currentStepSelection,
    updateCurrentStepSelection,
    hasTowSelected,
  } = useLocalityGuidedSearchContext();

  // Track vehicle info for towable
  const [vehicleInfo, setVehicleInfo] = useState<{
    year?: number;
    make?: string;
    modelId?: number;
    towingCapacity?: number;
  }>({});

  // Restore vehicle info from sessionStorage on mount
  useEffect(() => {
    if (window && window.sessionStorage) {
      const savedVehicleInfo = window.sessionStorage.getItem('guidedSearch1VehicleInfo');
      if (savedVehicleInfo) {
        setVehicleInfo(JSON.parse(savedVehicleInfo));
      }
    }
  }, []);

  // Handler for vehicle changes that auto-advances when model is selected
  const handleVehicleChange = (newVehicleInfo: {
    year?: number;
    make?: string;
    modelId?: number;
    towingCapacity?: number;
  }) => {
    setVehicleInfo(newVehicleInfo);

    // Save vehicle info to sessionStorage
    if (window && window.sessionStorage) {
      window.sessionStorage.setItem('guidedSearch1VehicleInfo', JSON.stringify(newVehicleInfo));
    }

    // If a model was selected (has modelId and towingCapacity), auto-advance to search
    if (newVehicleInfo.modelId && newVehicleInfo.towingCapacity) {
      // Small delay to allow the UI to show the selected value before redirect
      setTimeout(() => {
        customApplySelection(newVehicleInfo);
      }, 300);
    }
  };

  // Custom apply selection that builds URL directly
  const customApplySelection = (vehicleInfoOverride?: {
    year?: number;
    make?: string;
    modelId?: number;
    towingCapacity?: number;
  }) => {
    // Use override if provided (for immediate redirect), otherwise use state
    const vehicleData = vehicleInfoOverride || vehicleInfo;

    // Get the current selections from sessionStorage (same as guided search context uses)
    const savedState = window.sessionStorage?.getItem('guidedSearch1State');
    const stepsSelection = savedState ? JSON.parse(savedState) : {};

    // Build parameters from selections
    const guidedSearchParams = buildParamsFromSelections(stepsSelection);

    // Build the search URL
    const searchUrl = new URL('/rv-search', window.location.origin);

    // Add guided search parameters
    Object.entries(guidedSearchParams).forEach(([key, value]) => {
      if (value && value !== 'undefined') {
        searchUrl.searchParams.set(key, String(value));
      }
    });

    // Add locality address
    if (headerAddress) {
      searchUrl.searchParams.set('address', headerAddress);
    }

    // Add vehicle info if present (for towable)
    // The main filter is trailerWeight[lte] based on the towing capacity
    if (vehicleData.towingCapacity) {
      searchUrl.searchParams.set('trailerWeight[lte]', String(vehicleData.towingCapacity));
    }

    // Also include vehicle info for display/tracking purposes
    // The towing_id is what the search page uses to restore the vehicle selection
    if (vehicleData.modelId) {
      searchUrl.searchParams.set('towing_id', String(vehicleData.modelId));
    }
    if (vehicleData.year) {
      searchUrl.searchParams.set('towable_year', String(vehicleData.year));
    }
    if (vehicleData.make) {
      searchUrl.searchParams.set('towable_make', vehicleData.make);
    }

    // Hard redirect
    window.location.href = searchUrl.toString();
  };

  // Check if we should auto-advance after selection (like original guided search)
  const moveToNextStepAfterSelection = useMemo(() => {
    if (!currentStepComponent) return false;

    return (
      currentStepComponent.fields.length === 1 &&
      currentStepComponent.fields.every(
        (field: any) => field.component_type === EGuidedSearchComponentType.ButtonGroupRadio,
      )
    );
  }, [currentStepComponent]);

  // Custom update selection that handles auto-completion
  const customUpdateCurrentStepSelection = (fieldId: string, value?: string | string[]) => {
    // Call the original update function (which now has towable logic built in)
    updateCurrentStepSelection(fieldId, value);

    // Check if this is a tow selection synchronously
    const isLastStep = currentStep === numberOfSteps;
    const isTowSelection = fieldId === 'tow-drive-delivery' && value === '1';

    // If this is the last step and should auto-advance, complete the flow
    // BUT don't redirect if Tow was just selected
    if (moveToNextStepAfterSelection && isLastStep && !isTowSelection) {
      // Wait for the selection to be saved to sessionStorage, then redirect
      setTimeout(() => {
        customApplySelection();
      }, 50);
    }
  };

  const everyStepSelectionApplied = useMemo(() => {
    if (!currentStepComponent || !currentStepSelection) return false;
    return currentStepComponent.fields.every(field => currentStepSelection[field.id]);
  }, [currentStepComponent, currentStepSelection]);

  if (!currentStepComponent) {
    return null;
  }

  const handleViewAllRVsClick = () => {
    if (locality?.address) {
      // Track the analytics event for skipping guided search
      trackRenterSearchedEvent({
        source: 'guided_search_1_locality_skipped',
        surfacedLocations: LocationGroupType.OTHER,
        totalAdults: 0,
        totalChildren: 0,
        totalInfants: null,
        locationQuery: locality.address,
        isADAAccessible: false,
        isPetFriendly: false,
        flexibleDateRange: null,
        departureDate: null,
        returnDate: null,
        hasCategories: true,
        selectedCategory: EHeaderTab.ALL,
        towableYear: null,
        towableMake: null,
        towableModel: null,
        rentalType: ERentalType.RV,
        searchFilterCategory: null,
        lastQuestionSeen: null,
      });

      const searchUrl = new URL('/rv-search', window.location.origin);
      searchUrl.searchParams.set('address', locality.address);
      window.location.href = searchUrl.toString();
    }
  };

  return (
    <div className="w-full bg-neutral-05">
      <div className="px-4">
        <div className="max-w-md mx-auto">
          <div className="p-4 bg-white border border-gray-200 shadow-lg lg:p-6 rounded-2xl">
            <h2 className="text-xl font-semibold text-center text-gray-900">
              {currentStepComponent.properties.title}
            </h2>

            {/* Progress bar */}
            <div className="relative mx-0 my-8 md:mx-auto md:w-1/2 h-[4px]">
              <div className="absolute inset-0 bg-neutral-20" />
              <div
                className="absolute top-0 bottom-0 left-0 bg-green-500"
                style={{ width: `${(currentStep / numberOfSteps) * 100}%` }}
              />
            </div>

            <GuidedSearchComponent
              {...currentStepComponent}
              values={currentStepSelection || {}}
              onChange={customUpdateCurrentStepSelection}
            />

            {/* Towable vehicle selector - only show when Tow is selected on page-4 */}
            {hasTowSelected && (
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  animation: 'slideDown 300ms ease-in-out',
                }}>
                <TowableVehicleSelector
                  key="towable-vehicle-selector"
                  onVehicleChange={handleVehicleChange}
                  initialVehicleInfo={vehicleInfo}
                />
              </div>
            )}
            <style jsx>{`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  max-height: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  max-height: 1000px;
                  transform: translateY(0);
                }
              }
            `}</style>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200 lg:pt-6 lg:mt-8">
              <LinkButton
                label={intl.formatMessage({ defaultMessage: 'Back', id: 'cyR7Kh' })}
                className={clsx({ invisible: !goToPrevStep })}
                onClick={goToPrevStep}
              />

              <Button
                label={intl.formatMessage({ defaultMessage: 'Next', id: '9+Ddtu' })}
                variant={EButtonColorVariant.Primary}
                onClick={() => (goToNextStep || customApplySelection)()}
                disabled={!everyStepSelectionApplied}
              />
            </div>
          </div>

          {/* View all RVs link */}
          {locality && (
            <div className="mt-6 text-center">
              <LinkButton
                label={intl.formatMessage(
                  {
                    id: 'sjWvIJ',
                    defaultMessage: 'Search all {locationName} RVs',
                    description: 'Locality guided search > Link to search all RVs in the location',
                  },
                  { locationName: locality.name },
                )}
                onClick={handleViewAllRVsClick}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom hook to build parameters from guided search selections
const useGuidedSearchParams = (initialGuidedSearchData?: any) => {
  const { data: guidedSearchData } = useGuidedSearchComponents(initialGuidedSearchData);

  const buildParamsFromSelections = (
    stepsSelection: Record<string, Record<string, string | string[] | undefined>>,
  ) => {
    if (!guidedSearchData?.data_file.components || !stepsSelection) return {};

    const guidedSearchStepsData = guidedSearchData.data_file.components.reduce<
      TGuidedSearchComponent[]
    >((acc, component) => {
      const fields = component.fields.filter((field: any) =>
        Object.values(EGuidedSearchComponentType).includes(field.component_type),
      );

      if (fields.length) {
        return [...acc, { ...component, fields }];
      }
      return acc;
    }, []);

    const params: Record<string, string> = {};

    guidedSearchStepsData.forEach(step => {
      if (!stepsSelection[step.id]) return;

      step.fields.forEach((field: any) => {
        if (!stepsSelection[step.id]?.[field.id]) return;

        if (field.component_type === EGuidedSearchComponentType.ButtonGroupRadio) {
          const selectedOption = field.properties.rows.find(
            (row: any) => row.id === stepsSelection[step.id]?.[field.id],
          );

          if (selectedOption) {
            selectedOption.query_params.forEach((param: any) => {
              params[param.key] = params[param.key]
                ? `${params[param.key]},${param.value}`
                : param.value;
            });
          }
        }
      });
    });

    if (params['delivery']) {
      params['deliveryStationary'] = EDeliveryOption.MOVING;
    }

    return params;
  };

  return { buildParamsFromSelections };
};

const InlineGuidedSearch: React.FC<InlineGuidedSearchProps> = ({
  initialParams,
  locality,
  source = 'guided_search_1_locality',
  guidedSearchData,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const headerSearchFilters = useSelector(getHeaderSearchFilters);
  const { buildParamsFromSelections } = useGuidedSearchParams(guidedSearchData);

  // Set initial search parameters in the header search filter
  // This ensures the locality location is available to the guided search
  useEffect(() => {
    if (initialParams?.address && typeof initialParams.address === 'string') {
      dispatch(
        setHeaderSearchFilter({
          address: initialParams.address,
          skip_defaults: true,
        }),
      );
    }
  }, [dispatch, initialParams?.address]);

  const address =
    headerSearchFilters.address ||
    (typeof initialParams?.address === 'string' ? initialParams.address : undefined);

  return (
    <LocalityGuidedSearchProvider source={source} guidedSearchData={guidedSearchData}>
      <ErrorBoundary
        fallBackComponent={null}
        onComponentDidCatch={() => {
          showToast(
            EToastVariant.Error,
            intl.formatMessage({ defaultMessage: 'Oops!', id: 'BEbOqj' }),
            intl.formatMessage({
              defaultMessage: 'Something went wrong, please try again.',
              id: '8GvVWZ',
            }),
          );
        }}>
        <InlineGuidedSearchContent
          locality={locality}
          buildParamsFromSelections={buildParamsFromSelections}
          headerAddress={address}
        />
      </ErrorBoundary>
    </LocalityGuidedSearchProvider>
  );
};

export default InlineGuidedSearch;
